import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, Output, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { PropertyCardSimple } from '../property-card-simple/property-card-simple';
import { PagedResponse, Property, PropertyImage, PropertySearchRequest } from '../../models/property.model';
import { BehaviorSubject, catchError, combineLatest, map, merge, of, switchMap } from 'rxjs';
import { PropertyService } from '../../services/property.service';
import { AuthService } from '../../../../core/services/auth.service';
import { PropertyLikeService } from '../../services/property-like.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Location } from '../../../../shared/general/models/location.model';
import { GoogleMapComponent } from '../../../../shared/general/components/google-map/google-map';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule, MatFabButton } from '@angular/material/button';
import { PropertyFilterDialog } from '../property-filter-dialog/property-filter-dialog';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { GoogleMapFullComponent } from '../../../../shared/general/components/google-map-full/google-map-full';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HapticsService } from '../../../../shared/general/services/haptics.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { SeoService } from '../../../../shared/services/seo.service';
import { environment } from '../../../../../environments/environment';
import { PropertyFilter } from '../property-filter/property-filter';
import { LoaderComponent } from '../../../../shared/general/components/loader/loader.component';

@Component({
  selector: 'app-property-list',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFabButton,
    PropertyCardSimple,
    GoogleMap,
    GoogleMapsModule,
    GoogleMapComponent,
    MatDialogModule,
    // PropertyLayoutComponent,
    FormsModule,
    GoogleMapFullComponent,
    MatSidenavModule,
    MatNavList,
    MatListItem,
    MatToolbarModule,
    RouterOutlet,
    PropertyFilter,
    LoaderComponent
],
  templateUrl: './property-list.html',
  styleUrl: './property-list.scss',
})
export class PropertyListComponent implements OnInit, AfterViewInit {
  private propertyService = inject(PropertyService);
  private authService = inject(AuthService);
  private likeService = inject(PropertyLikeService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private gtm = inject(GtmService);
  private cdr = inject(ChangeDetectorRef);
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private haptics = inject(HapticsService);

  platformId = inject(PLATFORM_ID);
  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('filterSidenav') filterSidenav!: MatSidenav;
  @Output() selectedProperty: Property | undefined = undefined;
  private seo = inject(SeoService);
  properties: Property[] = [];
  locations: Location[] = [];
  map_height = '95vh';
  selected_property = signal<Property | null>(null);
  totalItems = 0;
  loading = false;
  searchRequest: PropertySearchRequest = { page: 0, size: 30 };
  isMobile = false;
  isBrowser = false;
  private refreshSearch$ = new BehaviorSubject<void>(undefined);
  
  ngOnInit(): void {
    this.isBrowser = this.platformId === 'browser';
    if (this.platformId === 'browser') {
      document.body.scrollTop = 0;
      
      this.gtm.pageView({
        page_path: '/property',
        page_title: 'Property Search | Horizinns'
      });

      this.breakpointObserver.observe([Breakpoints.Handset])
        .subscribe(result => {
          this.isMobile = result.matches;
        });
      
      this.route.queryParams.subscribe(params => {
        const city = params['city'];
        const state = params['state'];
        const lat = params['lat'];
        const lng = params['lng'];
        const radius = params['radius'];
        const maxBedrooms = params['maxBedrooms'];
        const minBedrooms = params['minBedrooms'];
        const maxSleeps = params['maxSleeps'];
        const minSleeps = params['minSleeps'];
        const maxBathrooms = params['maxBathrooms'];
        const minBathrooms = params['minBathrooms'];
        const maxCost = params['maxCost'];
        const minCost = params['minCost'];

        if (city || state || (lat && lng)) {
          this.searchRequest.city = city ?? undefined;
          this.searchRequest.state = state;
          this.searchRequest.lat = lat ? +lat : undefined;
          this.searchRequest.lng = lng ? +lng : undefined;
          this.searchRequest.radius = radius ? +radius : (lat && lng) ? 50 : undefined;
          this.searchRequest.page = 0;
        } 
        if (maxBedrooms || minBedrooms) {
          this.searchRequest.maxBedrooms = maxBedrooms ? +maxBedrooms : undefined;
          this.searchRequest.minBedrooms = minBedrooms ? +minBedrooms : undefined;
        }
        if (maxSleeps || minSleeps) {
          this.searchRequest.maxSleeps = maxSleeps ? +maxSleeps : undefined;
          this.searchRequest.minSleeps = minSleeps ? +minSleeps : undefined;
        }
        if (maxBathrooms || minBathrooms) {
          this.searchRequest.maxBathrooms = maxBathrooms ? +maxBathrooms : undefined;
          this.searchRequest.minBathrooms = minBathrooms ? +minBathrooms : undefined;
        }
      });
    }
    this.seo.updateMetaTags('Property Search', 'Explore available properties on Horizinns', undefined, `${environment.appUrl}/property`);
    this.seo.addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Property Search",
      "description": "Explore available properties on Horizinns",
      "url": "${environment.appUrl}/property"
    });
    setTimeout(() => {
      this.refreshSearch$.next();   // or just this.refreshSearch$.next(undefined)
    }, 0);
    this.startPropertyPipeline();
    this.cdr.detectChanges();
  }
  
  ngAfterViewInit(): void {
    merge(this.sidenav.openedStart, this.sidenav.closedStart)
      .subscribe(() => {
        this.haptics.click();
      });
  }
  
  showList() {
    this.haptics.click();
    this.sidenav.open();
  }
  /** -----------------------------------------------------------------
   *  Reactive pipeline – replaces the whole setTimeout mess
   *  ----------------------------------------------------------------- */
  private startPropertyPipeline(): void {
    //console.log('[Pipeline] starting combineLatest');

  this.loading = true;

  combineLatest([
    this.authService.user$,
    this.route.queryParams,
    this.refreshSearch$,
  ]).pipe(
    // tap(([user, params, refresh]) => {
    //   //console.log('[Pipeline] combineLatest emitted →', {
    //     hasUser: !!user,
    //     params: !!params,
    //     refreshTick: refresh !== undefined
    //   });
    // }),

      // Force <any, any> to break the inference loop causing 'unknown' errors
      switchMap<any, any>(([user]) => {
        return this.propertyService.searchProperties({ ...this.searchRequest }).pipe(
          map(page => ({ 
            page: page, 
            userId: user?.id ?? null 
          })),
          catchError((err) => {
            // This catches the 'sleeps' attribute error from the Spring backend
            //console.error('Property search failed:', err);
            this.loading = false;
            this.cdr.detectChanges();

            // Create a full mock of PagedResponse to satisfy the interface
            const emptyPage: PagedResponse<Property> = {
              content: [],
              totalElements: 0,
              totalPages: 0,
              size: this.searchRequest.size || 30,
              number: 0,
              first: true,
              last: true,
              numberOfElements: 0,
              empty: true,
              pageable: {} as any,
              sort: { empty: true, sorted: false, unsorted: true } as any // ADDED THIS
            };

            return of({ 
              page: emptyPage, 
              userId: user?.id ?? null 
            });
          })
        );
      }),

      switchMap((data: any) => {
        const { page, userId } = data;
        if (!userId) {
          return of({ page, likedPropertyIds: [] as string[] });
        }
        return this.likeService.getUserLikes(userId).pipe(
          map(likes => ({
            page,
            likedPropertyIds: (likes as any[]).map((l: any) => l.propertyId)
          })),
          catchError(() => of({ page, likedPropertyIds: [] as string[] }))
        );
      }),

      map((data: any) => {
        const { page, likedPropertyIds } = data;
        const props = page.content.map((p: any) => ({
          ...p,
          liked: likedPropertyIds.includes(p.id)
        }));
        return { props, totalElements: page.totalElements };
      })
    ).subscribe({
      next: ({ props, totalElements }) => {
        //console.log('next', { props, totalElements });
        if (isPlatformBrowser(this.platformId)) {
          document.body.scrollTop = 0;
        }
        this.properties = props; 
        this.totalItems = totalElements; 
        this.buildMapLocations(); 
        this.loading = false; 
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Fatal Pipeline Error:', err);
        console.error('[Pipeline] ERROR:', err);
        this.loading = false; 
        this.cdr.detectChanges();

      },
      complete: () => console.log('[Pipeline] completed (should not happen)')
    });
  }
  
  /** -----------------------------------------------------------------
   *  Build the array used by the map component
   *  ----------------------------------------------------------------- */
  private buildMapLocations(): void {
    this.locations = this.properties.map(p => {
      //console.log('property id', p.id);
      const primaryImg = p.images?.find(i => i.sortOrder === 0) ?? p.images?.[0];
      const name = p.displayName ? this.getDisplayName(p.displayName) : this.getDisplayName(p.propertyType);
      return {
        id: p.id ?? null,
        lat: p.address.lat,
        lng: p.address.lng,
        name: `${ name } in ${ p.address.city }, ${ p.address.state }`,
        imageUrl: primaryImg ? primaryImg.imageUrl : 'assets/placeholder.jpg',
        liked: p.liked ?? false,
        property: p
      };
    });
  }

  getDisplayName(str: string): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
  }
  /** -----------------------------------------------------------------
   *  Pagination handler (MatPaginator)
   *  ----------------------------------------------------------------- */
  onPageChange(event: any): void {
    this.searchRequest.page = event.pageIndex;
    this.searchRequest.size = event.pageSize;
    this.startPropertyPipeline();   // reload with new page
  }

  filterResults = () => {
    console.log('filterResults');
    const dialogRef = this.dialog.open(PropertyFilterDialog, {
      panelClass: 'property-filter-dialog-panel',
      width: '400px',
      hasBackdrop: false
    });

    dialogRef.componentInstance.cancel.subscribe(() => {
      console.log('Cancelled');
      dialogRef.close();
    });
  }

  onThumbnailClick(image: PropertyImage | string): void {
    this.haptics.click();
    if (typeof image === 'string') {
      window.open(image, '_blank');
    } else {
      // Open modal or navigate to image detail
      console.log('Selected image:', image);
    }
  }

  onPropertyLiked(property: Property): void { // ✅ ADD THIS METHOD
    const index = this.properties.findIndex(p => p.id === property.id);
    if (index !== -1) {
      this.properties[index] = { ...property }; // ✅ UPDATES UI!
    }
    console.log('UI Updated:', property.id, property.liked);
  }

  onSelectedProperty(property: Property): void {
    this.haptics.click();
    // console.log('output selectedProperty', property);
    // //this.selectedProperty.emit(property);
    // //this.selected_property.set(property);
    this.selectedProperty = property;
    setTimeout(() => {
      document.getElementById('selected')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest'
      });
    }, 400);
    const index = this.properties.findIndex(p => p.id === property.id);
    if (index !== -1) {
      //this.selectedProperty.emit(property);
    }
  }

  selectProperty(property: Property) {
    this.haptics.click();
    if (this.selectedProperty?.id === property?.id) {
      this.selectedProperty = undefined;
    } else {
      this.selectedProperty = property;
    }
  }

  onMapWindowClosed() {
    this.selectedProperty = undefined; // Resetting this allows the next click to be "new"
  }

  onPropertySelected(property: Property): void {
    this.selectedProperty = property;
  }

  viewProperty(id: string) {
    this.haptics.click();
    this.router.navigate(['/property', id]);
  }

  closeSideNav() {
    this.haptics.click();
    console.log('closesideNav click');
    if (isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
    }
    this.sidenav.close()
  }

  onFilterApplied(filters: Partial<PropertySearchRequest>) {
    this.haptics.click();
    this.filterSidenav.close();
    if (isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
    }
    // Create the new state
    const updatedRequest = { 
      ...this.searchRequest, 
      ...filters, 
      page: 0 
    };

    // Update the local variable AND the stream at the same time
    this.searchRequest = updatedRequest;
    this.loading = true; // Force UI state
    this.refreshSearch$.next();
  }

  private filterLabels: Record<string, string> = {
    city: 'City',
    state: 'State',
    minBedrooms: 'Beds',
    maxBedrooms: 'Max Beds',
    minBathrooms: 'Baths',
    maxBathrooms: 'Max Baths',
    minSleeps: 'Sleeps',
    maxSleeps: 'Max Sleeps'
  };

  getActiveFilters() {
    return Object.keys(this.searchRequest)
      .filter(key => {
        const val = (this.searchRequest as any)[key];
        // Only show pills for values that aren't the defaults or undefined
        if (val === undefined || val === null || val === '') return false;
        if (key === 'page' || key === 'size' || key === 'radius') return false;
        return true;
      })
      .map(key => ({
        key,
        label: this.filterLabels[key] || key,
        value: (this.searchRequest as any)[key]
      }));
  }

  getActiveFilterCount(): number {
    return this.getActiveFilters().length;
  }

  removeFilter(key: string) {
    const updatedRequest = { ...this.searchRequest };
    delete (updatedRequest as any)[key];
    
    // Reset numeric ranges to defaults if removed
    if (key.includes('min') || key.includes('max')) {
      (updatedRequest as any)[key] = undefined;
    }

    this.onFilterApplied(updatedRequest);
  }

  clearAllFilters() {
    this.onFilterApplied({ page: 0, size: 30 });
  }
}
